import { useEffect, useRef, useState } from "react";
import InvoiceTable from "Pages/AdminInterface/clients/client/invoices/components/InvoiceTable";
import dayjs from "dayjs";
import usePost from "Hooks/usePost";
import { getInvoicesByClientId, createInvoice } from "API/swesum";
import { Grid } from "@mui/material";
import DateWithClear from "Components/UI/Form/DateWithClear";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { openStatusOptions } from "utils/options/openStatusOptions";
import { SUB_STATUS } from "utils/options/SUB_STATUS";
import useInvoiceFormOptions from "Components/Invoice/useInvoiceFormOptions";
import { useEndCustomers } from "Context/EndCustomers";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import CreateInvoiceModal from "Components/UI/Modals/CreateInvoiceModal";
import createFilters from "utils/invoicesFilterParser";
import Checkbox from "@mui/material/Checkbox";

export default function ClientInvoices({ client }) {
  const { openSuccess, openError } = usePopupNotificationContext();
  const [invoices, setInvoices] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [openStatus, setOpenStatus] = useState(0);
  const [subStatus, setSubStatus] = useState(-1);
  const [pausedStatus, setPausedStatus] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [totalRows, setTotalRows] = useState(30);
  const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);
  const [order, setOrder] = useState({ Column: "InvoiceDate", Order: 1 });
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const {
    loading,
    options,
    formData: invoiceFormData,
    handleInputChange,
  } = useInvoiceFormOptions(client.Id);
  const prevFilters = useRef({
    startDate,
    endDate,
    client,
    openStatus,
    subStatus,
    pausedStatus,
  });

  const { endCustomers, fetchEndCustomers } = useEndCustomers();
  const handleOpenCreateInvoice = () => setCreateInvoiceModalOpen(true);
  const handleCloseCreateInvoice = () => setCreateInvoiceModalOpen(false);

  const createInvoiceReq = usePost({
    mtnFunc: (invoiceFormData) => createInvoice(invoiceFormData),
    onError: () => {
      openError("Ett fel uppstod när fakturan skapades.", 3000);
    },
    onSuccess: (data) => {
      openSuccess("Fakturan har skapats.", 3000);
      setInvoices([...invoices, data]);
    },
  });

  const getInvoicesByClientIdReq = async (
    force = false,
    searchTerm = null,
    pageOverride = null
  ) => {
    setLoadingInvoices(true);
    try {
      let filters = createFilters({
        startDate: startDate,
        endDate: endDate,
        openStatus: openStatus,
        subStatus: subStatus,
        pausedStatus: pausedStatus,
        searchTerm: searchTerm,
      });

      const newInvoices = await getInvoicesByClientId(client.Id, {
        Filters: filters,
        Order: order,
        Page: pageOverride !== null ? pageOverride : currentPage,
        PageSize: subStatus === -1 ? rowsPerPage : 0,
      });

      setTotalRows(newInvoices.Count);
      if (force) {
        setInvoices(newInvoices.Rows);
        setCurrentPage(0);
      } else {
        const invoiceMap = new Map(
          newInvoices.Rows.map((invoice) => [invoice.Id, invoice])
        );
        const mergedInvoices = [
          ...new Map(
            [
              ...invoices.filter((invoice) => !invoiceMap.has(invoice.Id)),
              ...newInvoices.Rows,
            ].map((invoice) => [invoice.Id, invoice])
          ).values(),
        ];

        setInvoices(mergedInvoices);
      }
    } catch (err) {
      console.error(err);
      openError("Ett fel uppstod", 3000);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const updateRows = () => {
    getInvoicesByClientIdReq();
  };

  const onDownload = async () => {
    try {
      let filters = createFilters({
        startDate: startDate,
        endDate: endDate,
        openStatus: openStatus,
        subStatus: subStatus,
        pausedStatus: pausedStatus,
        searchTerm: null,
      });

      const newInvoices = await getInvoicesByClientId(client.Id, {
        Filters: filters,
        Order: order,
        Page: currentPage,
        PageSize: 0,
      });
      return newInvoices;
    } catch (err) {
      openError("Ett fel uppstod", 3000);
    }
  };

  useEffect(() => {
    const filtersChanged =
      prevFilters.current.startDate !== startDate ||
      prevFilters.current.endDate !== endDate ||
      prevFilters.current.client !== client ||
      prevFilters.current.openStatus !== openStatus ||
      prevFilters.current.pausedStatus !== pausedStatus ||
      prevFilters.current.subStatus !== subStatus;
    getInvoicesByClientIdReq(filtersChanged);

    const getInvoicesReq = async () => {
      try {
        if (filtersChanged) {
          await getInvoicesByClientIdReq(true, null, 0);
          setCurrentPage(0);
        } else {
          await getInvoicesByClientIdReq(false);
        }
      } catch (err) {
        openError("Någonting gick fel vid hämtning av fakturor", 3000);
      }
    };

    prevFilters.current = {
      startDate,
      endDate,
      client,
      openStatus,
      subStatus,
      pausedStatus,
    };

    getInvoicesReq();
  }, [
    rowsPerPage,
    currentPage,
    startDate,
    endDate,
    client,
    order,
    openStatus,
    subStatus,
    pausedStatus,
  ]);

  useEffect(() => {
    fetchEndCustomers();
  }, []);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={2}>
          <DateWithClear
            label="Från"
            value={startDate === "" ? "" : dayjs(startDate)}
            onChange={(newVal) => {
              setStartDate(dayjs(newVal).format("YYYY-MM-DD 00:00:00"));
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <DateWithClear
            label="Till"
            value={dayjs(endDate)}
            onChange={(newVal) => {
              setEndDate(dayjs(newVal).format("YYYY-MM-DD 00:00:00"));
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <FormControl margin="dense" fullWidth sx={{ textAlign: "left" }}>
            <InputLabel>Stängd/Öppen</InputLabel>
            <Select
              name="OpenStatus"
              value={openStatus}
              onChange={(e) => setOpenStatus(e.target.value)}
              label="OpenStatus"
              size="small"
            >
              {openStatusOptions.map((options) => (
                <MenuItem key={options.Value} value={options.Value}>
                  {options.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <FormControl margin="dense" fullWidth sx={{ textAlign: "left" }}>
            <InputLabel>Substatus</InputLabel>
            <Select
              name="Substatus"
              value={subStatus}
              onChange={(e) => setSubStatus(e.target.value)}
              label="Substatus"
              size="small"
            >
              {SUB_STATUS.map((options) => (
                <MenuItem key={options.value} value={options.value}>
                  {options.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={2}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <FormControl margin="dense" fullWidth sx={{ textAlign: "left" }}>
            <InputLabel shrink>
              Endast pausade fakturor
            </InputLabel>
            <Checkbox
              disableRipple
              checked={pausedStatus}
              onChange={(e) => setPausedStatus(e.target.checked)}
              size="small"
            />
          </FormControl>
        </Grid>
      </Grid>
      <InvoiceTable
        invoices={invoices}
        onChangePage={(pageNr) => {
          setLoadingInvoices(true);
          setCurrentPage(pageNr);
        }}
        onChangeRowsPerPage={(invoiceNumber) => setRowsPerPage(invoiceNumber)}
        onChangeSort={(column, order) => {
          setLoadingInvoices(true);
          setOrder({ Column: column, Order: order });
        }}
        sortOrder={order}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        openCreateInvoice={handleOpenCreateInvoice}
        onUpdate={updateRows}
        onSearch={(searchTerm) => getInvoicesByClientIdReq(true, searchTerm)}
        invoicesLoading={loadingInvoices}
        getAllOnDownload={onDownload}
      />
      {!loading && options && (
        <CreateInvoiceModal
          endCustomers={endCustomers.sort((a, b) => a.Name - b.Name)}
          client={client}
          isOpen={createInvoiceModalOpen}
          handleClose={handleCloseCreateInvoice}
        />
      )}
    </>
  );
}
